import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Star, StarOff } from 'lucide-react';
import { getEnvVar } from '../../config/env';
import { ApiKeyModal } from './components/ApiKeyModal';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

const PROVIDER_OPTIONS = [
  { provider_type: 'voice', provider_name: 'elevenlabs', label: 'ElevenLabs', typeLabel: 'Voice', envVar: 'ELEVENLABS_API_KEY' },
  { provider_type: 'voice', provider_name: 'retell', label: 'Retell', typeLabel: 'Voice & Phone', envVar: 'RETELL_API_KEY' },
  { provider_type: 'phone', provider_name: 'telnyx', label: 'Telnyx', typeLabel: 'Phone', envVar: 'TELNYX_API_KEY' },
  { provider_type: 'llm', provider_name: 'openai', label: 'OpenAI', typeLabel: 'LLM', envVar: 'OPENAI_API_KEY' },
  { provider_type: 'llm', provider_name: 'anthropic', label: 'Anthropic', typeLabel: 'LLM', envVar: 'ANTHROPIC_API_KEY' },
  { provider_type: 'llm', provider_name: 'google', label: 'Google', typeLabel: 'LLM', envVar: 'GOOGLE_GENERATIVE_AI_API_KEY' },
  { provider_type: 'email', provider_name: 'resend', label: 'Resend', typeLabel: 'Email', envVar: 'RESEND_API_KEY' },
  { provider_type: 'email', provider_name: 'mailgun', label: 'Mailgun', typeLabel: 'Email', envVar: 'MAILGUN_API_KEY' },
];

interface ApiKey {
  id: string;
  provider_type: string;
  provider_name: string;
  name: string;
  description?: string;
  tags?: string[];
  is_global: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<'add' | 'edit' | null>(null);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [addModalProvider, setAddModalProvider] = useState<typeof PROVIDER_OPTIONS[0] | null>(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    loadKeys();
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const loadKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/api-keys`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load API keys');
      const data = await res.json();
      setKeys(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const getKeysForProvider = (providerType: string, providerName: string) =>
    keys.filter((k) => k.provider_type === providerType && k.provider_name === providerName);

  const getGlobalKeyForProvider = (providerType: string, providerName: string) =>
    keys.find((k) => k.provider_type === providerType && k.provider_name === providerName && k.is_global);

  const handleCreate = async (data: {
    providerType: string;
    providerName: string;
    name?: string;
    description?: string;
    tags?: string[];
    apiKey?: string;
    isGlobal?: boolean;
  }) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/api-keys`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        provider_type: data.providerType,
        provider_name: data.providerName,
        name: data.name || data.providerName,
        description: data.description,
        tags: data.tags,
        api_key: data.apiKey,
        is_global: data.isGlobal ?? false,
      }),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.detail || resp.error || 'Failed to save');
    setSuccess('API key added');
    setModalOpen(null);
    loadKeys();
  };

  const handleUpdate = async (apiKeyId: string, data: {
    name?: string;
    description?: string;
    tags?: string[];
    apiKey?: string;
    isGlobal?: boolean;
  }) => {
    const body: Record<string, unknown> = {};
    if (data.name !== undefined) body.name = data.name;
    if (data.description !== undefined) body.description = data.description;
    if (data.tags !== undefined) body.tags = data.tags;
    if (data.apiKey !== undefined) body.api_key = data.apiKey;
    if (data.isGlobal !== undefined) body.is_global = data.isGlobal;

    const res = await fetch(`${API_BASE_URL}/api/v1/admin/api-keys/${apiKeyId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.detail || resp.error || 'Failed to update');
    setSuccess('API key updated');
    setModalOpen(null);
    setEditingKey(null);
    loadKeys();
  };

  const handleSetGlobal = async (apiKeyId: string) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/api-keys/${apiKeyId}/set-global`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.detail || resp.error || 'Failed to set global');
    setSuccess('Set as global key');
    loadKeys();
  };

  const handleDelete = async (apiKeyId: string) => {
    if (!confirm('Remove this API key? Workspaces using it will fall back to global key.')) return;
    try {
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/api-keys/${apiKeyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      setSuccess('API key removed');
      loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const openAddModal = (provider?: typeof PROVIDER_OPTIONS[0]) => {
    setEditingKey(null);
    setModalOpen('add');
    setAddModalProvider(provider || null);
  };

  const openEditModal = (key: ApiKey) => {
    setEditingKey(key);
    setModalOpen('edit');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">API Keys Master</h1>
            <p className="mt-1 text-sm text-gray-600">
              Add all API keys here. Mark one key per provider as global (default). In workspace area, select which key applies.
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Add Key
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 text-green-700 text-sm">{success}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="space-y-8">
            {PROVIDER_OPTIONS.map((provider) => {
              const providerKeys = getKeysForProvider(provider.provider_type, provider.provider_name);
              const globalKey = getGlobalKeyForProvider(provider.provider_type, provider.provider_name);

              return (
                <div key={`${provider.provider_type}-${provider.provider_name}`} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {provider.label} <span className="text-gray-500 font-normal">({provider.typeLabel})</span>
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {providerKeys.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500">
                        No keys added. Add keys in API Keys module.
                        <button
                          onClick={() => openAddModal(provider)}
                          className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Add Key
                        </button>
                      </div>
                    ) : (
                      providerKeys.map((apiKey) => (
                        <div
                          key={apiKey.id}
                          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-900">{apiKey.name}</span>
                            {apiKey.is_global && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                <Star className="h-3 w-3" /> Global
                              </span>
                            )}
                            {apiKey.description && (
                              <span className="text-sm text-gray-500 truncate max-w-xs">{apiKey.description}</span>
                            )}
                            {apiKey.tags?.length ? (
                              <span className="inline-flex flex-wrap gap-1">
                                {apiKey.tags.map((t) => (
                                  <span key={t} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                    {t}
                                  </span>
                                ))}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2">
                            {!apiKey.is_global && (
                              <button
                                onClick={() => handleSetGlobal(apiKey.id)}
                                className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-sm"
                                title="Set as global"
                              >
                                <StarOff className="h-4 w-4" /> Set Global
                              </button>
                            )}
                            <button
                              onClick={() => openEditModal(apiKey)}
                              className="text-blue-600 hover:text-blue-900 inline-flex items-center gap-1 text-sm"
                            >
                              <Edit2 className="h-4 w-4" /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(apiKey.id)}
                              className="text-red-600 hover:text-red-900 inline-flex items-center gap-1 text-sm"
                            >
                              <Trash2 className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          <strong>Flow:</strong> Add keys here → Mark one per provider as global → In User Detail → Workspaces → API Keys, select which key applies per workspace. Default is global key.
        </div>
      </div>

      {modalOpen === 'add' && (
        <ApiKeyModal
          provider={addModalProvider || PROVIDER_OPTIONS[0]}
          allProviders={PROVIDER_OPTIONS}
          isAddMode={true}
          existingConfig={null}
          onClose={() => setModalOpen(null)}
          onSave={handleCreate}
          showIsGlobal={true}
        />
      )}
      {modalOpen === 'edit' && editingKey && (
        <ApiKeyModal
          provider={PROVIDER_OPTIONS.find((p) => p.provider_type === editingKey.provider_type && p.provider_name === editingKey.provider_name) || PROVIDER_OPTIONS[0]}
          allProviders={PROVIDER_OPTIONS}
          isAddMode={false}
          existingConfig={{
            id: editingKey.id,
            name: editingKey.name,
            description: editingKey.description,
            tags: editingKey.tags,
            isGlobal: editingKey.is_global,
          }}
          onClose={() => { setModalOpen(null); setEditingKey(null); }}
          onSave={(data) => handleUpdate(editingKey.id, data)}
          showIsGlobal={true}
        />
      )}
    </div>
  );
};
